import { lotame } from '@tunein/web-common';
import isEmpty from 'lodash/isEmpty';
import { BRAZE_FOLLOW_EVENT, BRAZE_UNFOLLOW_EVENT } from 'src/common/constants/braze';
import { selectIsBrazeInitialized } from '../selectors/app';
import { brazeLogCustomEvent } from '../utils/braze/brazeHelper';
import { getFavoriteMessageText } from '../utils/getFavoriteMessageText';
import { logClientError } from './logging';
import { showMessage } from './messagePopover';
import makeFavoriteActions from './utils/makeFavoriteActions';

export const FAVORITE_CURRENTLY_PLAYING = 'FAVORITE_CURRENTLY_PLAYING';
export const UNFAVORITE_CURRENTLY_PLAYING = 'UNFAVORITE_CURRENTLY_PLAYING';
export const FAVORITE_CONTAINER_ITEM = 'FAVORITE_CONTAINER_ITEM';
export const UNFAVORITE_CONTAINER_ITEM = 'UNFAVORITE_CONTAINER_ITEM';
export const FAVORITE_PROFILE = 'FAVORITE_PROFILE';
export const UNFAVORITE_PROFILE = 'UNFAVORITE_PROFILE';
export const REMOVE_FOLDER = 'REMOVE_FOLDER';

/**
 * creates an action which makes the favorite call to the favorites api
 * @param type  - aciton type
 * @param guideId - guideId of the item being favorited/unfavorited
 * @param meta - metadata to be used in the reducer for this action
 * @returns {object}
 */
export const favoriteEffect =
  (type, guideId, meta) =>
  (dispatch, getState, { services }) => {
    const state = getState();
    const isBrazeInitialized = selectIsBrazeInitialized(state);
    const nowPlaying = services.stores.playerStore.getState().metadata || {};

    const isFollowAction = [FAVORITE_PROFILE, FAVORITE_CURRENTLY_PLAYING].includes(type);

    if (isBrazeInitialized) {
      const brazeEvent = isFollowAction ? BRAZE_FOLLOW_EVENT : BRAZE_UNFOLLOW_EVENT;

      try {
        brazeLogCustomEvent(brazeEvent, {
          guideId,
          boostEnabledStation: !isEmpty(nowPlaying?.boost),
        });
      } catch (err) {
        dispatch(
          logClientError({
            message: `Braze Custom Event: ${brazeEvent} Error`,
            context: {
              error: err,
            },
          }),
        );
        // NOTE: Don't break app functionality for Braze
      }
    }

    return dispatch({
      type,
      api: {
        endpoint: ['user', 'toggleFavorite'],
        args: [{ guideId, favorite: isFollowAction }],
      },
      meta: {
        guideId,
        ...meta,
      },
    });
  };

const deleteFolderEffect = (folderId, meta) => (dispatch) => {
  return dispatch({
    type: REMOVE_FOLDER,
    api: {
      endpoint: ['user', 'deleteFolder'],
      args: [folderId],
    },
    meta: {
      ...meta,
    },
  });
};

/**
 * profile favorite actions
 */
export const { unFavorite: unFavoriteProfile, toggleFavorite: toggleProfileFavorite } =
  makeFavoriteActions(favoriteEffect, deleteFolderEffect);

/**
 * favorite action used in the player for the currently playing item
 * @param guideId
 * @returns {Function}
 */
export function favoriteCurrentlyPlayingItem(guideId = '') {
  return (dispatch, getState) => {
    const state = getState();
    const { nowPlaying = {} } = state.player;
    const favoriteMessages = getFavoriteMessageText(guideId, state);
    lotame.addInterests(nowPlaying);
    dispatch(favoriteEffect(FAVORITE_CURRENTLY_PLAYING, guideId, { nowPlaying }))
      .then(({ value: { status } }) => {
        if (status === 200) {
          dispatch(showMessage(favoriteMessages.favorited));
        } else {
          dispatch(showMessage(favoriteMessages.favoriteError));
        }
      })
      .catch(() => {
        dispatch(showMessage(favoriteMessages.favoriteError));
      });
  };
}

/**
 * unfavorite action used in the player for the currently playing item
 * @param guideId
 * @returns {Function}
 */
export function unFavoriteCurrentlyPlayingItem(guideId) {
  return (dispatch, getState) => {
    const { nowPlaying } = getState().player;
    const favoriteMessages = getFavoriteMessageText(guideId, getState());
    dispatch(favoriteEffect(UNFAVORITE_CURRENTLY_PLAYING, guideId, { nowPlaying }))
      .then(({ value: { status } }) => {
        if (status === 200) {
          dispatch(showMessage(favoriteMessages.unFavorited));
        } else {
          dispatch(showMessage(favoriteMessages.unFavoriteError));
        }
      })
      .catch(() => {
        dispatch(showMessage(favoriteMessages.unFavoriteError));
      });
  };
}
